import type { TeamMember } from '../../../content/landings/types';
import { LocaleLink } from '../../../i18n/locale-link';
import { LandingSection } from '../layout/landing-section';

interface Props {
  title: string;
  members: TeamMember[];
  fineprint: { label: string; href: string | null }[];
  onActionClick: (label: string) => void;
}

/**
 * «Кто за этим стоит» — возврат к дизайну мокапа (`landing-pro.html`, секция
 * `#trust`, строки 617–644): белая карточка, 132px круглое фото с брендовым
 * кольцом слева, имя/роль справа, контакты — кнопками внутри карточки.
 * Мокап держал ОДНОГО основателя (второй тогда ещё не был добавлен) — здесь
 * по карточке на каждого: CEO и CTO рядом на десктопе, в столбик на
 * телефоне. Легальные чипы (компания, ИНН, город, реквизиты) — отдельной
 * строкой под карточками, как в макете.
 *
 * Отступление 6 (ревью владельца, 2026-08-18): карточка больше НЕ несёт
 * абзац `bio` — читался как автогенерированный текст рядом с живым фото.
 */
export function Trust({ title, members, fineprint, onActionClick }: Props) {
  return (
    <LandingSection id="trust" top="sm">
      <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em] text-ink">
        {title}
      </h2>

      {/* Карточки — только фото + имя + роль, без кнопок и без абзаца.
          Контакты вынесены в общий ряд ниже: кнопки внутри одной карточки
          удлиняли её относительно второй, и пара разъезжалась по высоте.
          Теперь обе карточки структурно одинаковы и выравниваются сами. */}
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        {members.map((member) => (
          <div
            key={member.name}
            className="grid justify-items-center gap-6 rounded-2xl border border-edge bg-bone-surface p-7 text-center shadow-m-sm sm:grid-cols-[132px_1fr] sm:items-center sm:justify-items-start sm:text-left"
          >
            {member.photo ? (
              <img
                src={member.photo}
                alt={`${member.name}, ${member.role}`}
                width={640}
                height={640}
                loading="lazy"
                className="h-[132px] w-[132px] rounded-full border-[3px] border-brand-light object-cover"
              />
            ) : (
              <div
                aria-hidden="true"
                className="flex h-[132px] w-[132px] items-center justify-center rounded-full border-[3px] border-brand-light bg-brand text-3xl font-extrabold text-white"
              >
                {member.initials}
              </div>
            )}
            <div>
              <h3 className="text-[21px] font-extrabold leading-[1.12]">{member.name}</h3>
              <div className="text-sm font-semibold text-ink-muted">{member.role}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Общий ряд контактов под карточками. Собран из ссылок всех участников
          (сейчас они только у Игоря), поэтому вынос не теряет ничьи контакты. */}
      {members.flatMap((m) => m.links).length > 0 && (
        <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
          {members
            .flatMap((m) => m.links)
            .map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => onActionClick(link.label)}
                className={`block rounded-md px-[22px] py-3 text-center text-[15px] font-bold no-underline transition-colors sm:inline-block ${
                  link.primary
                    ? 'bg-brand text-white hover:bg-brand-mid'
                    : 'bg-brand-light text-brand hover:bg-[#DFEBE9]'
                }`}
              >
                {link.label}
              </a>
            ))}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {fineprint.map((item) =>
          item.href ? (
            <LocaleLink
              key={item.label}
              to={item.href}
              className="inline-flex items-center rounded-full border border-edge bg-bone-surface px-3.5 py-1.5 text-[12.5px] font-semibold text-ink-muted no-underline shadow-m-sm"
            >
              {item.label}
            </LocaleLink>
          ) : (
            <span
              key={item.label}
              className="inline-flex items-center rounded-full border border-edge bg-bone-surface px-3.5 py-1.5 text-[12.5px] font-semibold text-ink-muted shadow-m-sm"
            >
              {item.label}
            </span>
          ),
        )}
      </div>
    </LandingSection>
  );
}
